/**
 * Phase 14.5 Step 3 — Radar sub-form (controlled value/onChange leaf).
 *
 * Registry-driven from `radar.fields` (11 scalars):
 *   range_bias_m, range_bias_drift_m_per_s,
 *   bearing_bias_deg, bearing_bias_drift_deg_per_s,
 *   elevation_bias_deg, elevation_bias_drift_deg_per_s,
 *   range_noise_std_m, bearing_noise_std_deg, elevation_noise_std_deg,
 *   update_rate_hz, seed
 *
 * Held to the Step-2 invariant: no `useForm` here.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function _setField(value, field, raw, asInt, onChange) {
    const parsed = asInt ? parseInt(raw || "0", 10) : parseFloat(raw || "0");
    onChange?.({ ...(value || {}), [field]: Number.isFinite(parsed) ? parsed : 0 });
}

export default function RadarSubForm({ value, onChange, registry }) {
    const fields = Array.isArray(registry?.fields) ? registry.fields : [];
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="radar-subform">
            {fields.map((f) => {
                const isInt = f.unit === "int";
                const lo = f.physical_bounds?.lo;
                const hi = f.physical_bounds?.hi;
                const absMax = f.physical_bounds?.abs_max;
                return (
                    <div key={f.id} data-testid={`radar-field-${f.id}`}>
                        <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            {f.id} <span className="text-ux-text-3/60">({f.unit})</span>
                        </Label>
                        <Input
                            type="number"
                            step={isInt ? "1" : "any"}
                            min={lo ?? (typeof absMax === "number" ? -absMax : undefined)}
                            max={hi ?? absMax}
                            value={value?.[f.id] ?? 0}
                            onChange={(e) => _setField(value, f.id, e.target.value, isInt, onChange)}
                            data-testid={`radar-${f.id}`}
                            aria-label={f.id}
                        />
                        {f.physical_bounds?.note && (
                            <p className="mt-1 text-[11px] text-ux-text-3">{f.physical_bounds.note}</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
